import type { MessageKey } from './i18n';

// Codes raised by the SQL functions (`raise exception '<code>'`) and Edge Functions.
const KNOWN: Record<string, MessageKey> = {
  policy_version_required:'breaks.versionRequired',
  bank_configuration_invalid:'error.bankConfiguration',
  budget_invalid_hours:'member.quantityHours',
  recipient_unavailable:'error.deviceKeyNotActive',
  operation_id_required:'error.idempotencyConflict',
  report_leave_allocation_required:'report.allocationRequired',
  organization_invalid:'org.invalid',
  organization_cycle:'org.cycle',
  organization_stale:'org.stale',
  organization_busy:'org.busy',
  organization_in_use:'org.inUse',
  organization_limit:'org.limit',
  onboarding_incomplete:'wizard.fixRequired',
  employment_dates_invalid:'employment.datesInvalid',
  draft_invalid:'draft.invalid',
  draft_conflict:'draft.conflict',
  draft_completed:'draft.conflict',
  draft_unavailable:'draft.unavailable',
  draft_limit:'draft.limit',
  budget_invalid: 'budget.error',
  budget_retry_conflict: 'budget.stale',
  budget_stale: 'budget.stale',
  budget_required: 'budget.stale',
  budget_schedule_required: 'budget.scheduleRequired',
  budget_no_workdays: 'budget.noWorkdays',
  ccnl_overlap: 'companyCcnl.overlap',
  ccnl_in_use: 'companyCcnl.inUse',
  ccnl_close_invalid: 'companyCcnl.closeInvalid',
  ccnl_not_applicable_date: 'companyCcnl.invalid',
  invalid_filter: 'error.invalidValue',
  calendar_invalid: 'calendarConfig.invalid',
  allocation_invalid: 'allocation.invalid',
  allocation_total_mismatch: 'allocation.invalid',
  allocation_stale: 'allocation.stale',
  calendar_boundary_overlap: 'schedule.invalid',
  calendar_stale: 'calendarConfig.stale',
  calendar_retry_conflict: 'calendarConfig.stale',
  schedule_invalid: 'schedule.invalid',
  schedule_overlap: 'schedule.invalid',
  schedule_hours_mismatch: 'schedule.invalid',
  schedule_retry_conflict: 'employment.stale',
  schedule_stale: 'employment.stale',
  employment_stale: 'employment.stale',
  employment_date_invalid: 'employment.dateInvalid',
  employment_not_future: 'employment.notFuture',
  signup_not_allowed: 'error.signupNotAllowed',
  email_not_confirmed: 'error.emailNotConfirmed',
  forbidden: 'error.forbidden',
  not_authenticated: 'error.notAuthenticated',
  member_not_invitable: 'error.memberNotInvitable',
  member_email_missing: 'error.memberEmailMissing',
  last_owner: 'error.lastOwner',
  device_key_not_active: 'error.deviceKeyNotActive',
  upload_incomplete: 'error.uploadIncomplete',
  supersedes_invalid: 'error.supersedesInvalid',
  not_draft: 'error.notDraft',
  pdf_password: 'payroll.encryptedPdf',
  pdf_invalid: 'payroll.notPdf',
  crypto_selftest_failed: 'payroll.cryptoBroken',
  pairing_code_invalid: 'error.pairingCodeInvalid',
  not_pending: 'error.notPending',
  termination_date_required: 'error.terminationDateRequired',
  termination_date_invalid: 'error.terminationDateInvalid',
  email_locked: 'error.emailLocked',
  manager_invalid: 'error.managerInvalid',
  member_erased: 'error.memberErased',
  idempotency_conflict: 'error.idempotencyConflict',
  self_entry: 'error.selfEntry',
  member_not_active: 'error.memberNotActive',
  reason_required: 'error.reasonRequired',
  month_closed: 'monthClose.errClosed',
  month_has_pending: 'monthClose.errPending',
  month_not_over: 'monthClose.errNotOver',
  reopen_reason_required: 'monthClose.errReason',
  month_already_closed: 'monthClose.errClosed',
  delegation_invalid_person: 'delegations.errPerson',
  delegation_invalid_dates: 'delegations.errDates',
  delegation_overlap: 'delegations.errOverlap',
  restricted_period_protected_type: 'restricted.errProtected',
  restricted_period_invalid: 'restricted.errInvalid',
  leave_period_restricted: 'restricted.errBlocked',
  leave_type_needs_approval: 'leaveTypes.errNeedsApproval',
  leave_type_eligibility_fixed: 'leaveTypes.errEligibilityFixed',
  leave_type_not_eligible: 'leaveTypes.errNotEligible',
  grant_overlap: 'grants.errOverlap',
  grant_dates_invalid: 'grants.errDates',
  grant_type_invalid: 'error.invalidValue',
  leave_type_reserved: 'leaveTypes.errReserved',
  leave_type_exists: 'leaveTypes.errExists',
  leave_type_system: 'leaveTypes.errSystem',
  leave_type_required: 'leaveTypes.errRequired',
  leave_type_retired: 'leaveTypes.errRetired',
  leave_type_stale: 'leaveTypes.errStale',
  leave_type_in_use: 'leaveTypes.errInUse',
  leave_type_invalid: 'error.invalidValue',
  already_reversed: 'ledger.notReversible',
  not_reversible: 'ledger.notReversible',
  invalid_quantity: 'error.invalidValue',
  invalid_date: 'error.invalidValue',
  invalid_leave_type: 'error.invalidValue',
  ledger_append_only: 'ledger.notReversible',
  ts_in_future: 'error.tsInFuture',
  protocol_required: 'error.protocolRequired',
  leave_type_inactive: 'error.leaveTypeInactive',
  already_withdrawn: 'error.alreadyWithdrawn',
  answer_required: 'error.answerRequired',
  already_resolved: 'error.alreadyResolved',
  already_extended: 'error.alreadyExtended',
  deadline_passed: 'error.deadlinePassed',
  member_not_terminated: 'error.memberNotTerminated',
};

export function errorKey(err: unknown): MessageKey {
  const message = err instanceof Error ? err.message : typeof err === 'object' && err && 'message' in err ? String((err as { message: unknown }).message) : String(err);
  const code = typeof err === 'object' && err && 'code' in err ? String((err as { code: unknown }).code) : '';
  for (const [needle, key] of Object.entries(KNOWN)) if (message.includes(needle)) return key;
  if (code === '23505') return 'error.duplicate';
  if (code === '23514') return 'error.invalidValue';
  return 'error.generic';
}
